import { useWorkspaceText } from '../interface-copy';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { companyRequest } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/connection-access';
import { writeWorkspaceHash } from '@/lib/chat-links';
import { routeFor } from '../navigation';
import type { ConnectionReadItem } from '../connection-read-access/api';
import type { ConnectionWriteConsent } from '../../../src/connectors/read-access/index.mjs';

/**
 * Agent setup shows whether a connection lets the agent make changes. Writing is
 * turned on only in Connections (D73): this links there and reads the setting
 * again when the person comes back, but never grants it itself.
 */
export function ConnectionWriteSetup({
  connection,
  onAllowed,
}: {
  connection: Pick<ConnectionReadItem, 'connectionId' | 'name'> &
    Partial<Pick<ConnectionReadItem, 'canSet'>>;
  onAllowed: (connectionId: string) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(copyCatalog);
  const notified = useRef<string | null>(null);
  const [state, setState] = useState<{ id: string; consent: ConnectionWriteConsent | null } | null>(
    null,
  );
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const path = `/api/v1/company/connection-reads/${encodeURIComponent(connection.connectionId)}/write-access`;
  useEffect(() => {
    let active = true;
    setError(false);
    void companyRequest<{ consent: ConnectionWriteConsent | null }>(path)
      .then((value) => {
        if (active) setState({ id: connection.connectionId, consent: value.consent });
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [path, connection.connectionId, revision]);
  // The person sets Read & write in Connections, then returns to this page.
  useEffect(() => {
    const recheck = () => setRevision((value) => value + 1);
    window.addEventListener('focus', recheck);
    window.addEventListener('hashchange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      window.removeEventListener('hashchange', recheck);
    };
  }, []);
  const current = state?.id === connection.connectionId ? state : null;
  const allowed = Boolean(current && !current.consent);
  useEffect(() => {
    if (current && allowed && notified.current !== current.id) {
      notified.current = current.id;
      onAllowed(current.id);
    }
  }, [current, allowed, onAllowed]);
  const openConnections = () =>
    writeWorkspaceHash(
      `${routeFor('connections')}?access=${encodeURIComponent(connection.connectionId)}`,
    );
  return (
    <section className="ws-connection-write-setup">
      {error ? (
        <>
          <p role="alert">{copy.saveFailed}</p>
          <Button variant="outline" onClick={() => setRevision((value) => value + 1)}>
            {uiText('Refresh')}
          </Button>
        </>
      ) : allowed ? (
        <p role="status">{copy.whatReadWrite}</p>
      ) : current ? (
        <>
          <p>
            {formatCopy(connection.canSet === false ? copy.agentWriteAsk : copy.agentWriteNeeded, {
              name: connection.name,
            })}
          </p>
          <Button variant="outline" onClick={openConnections}>
            {copy.openConnections}
          </Button>
        </>
      ) : null}
    </section>
  );
}
